/**
 * Fetch-based SSE reader for `GET /agent/stream/:runId` (Slice 3d).
 *
 * Deliberately NOT the native `EventSource`:
 * - the UI needs the pre-stream HTTP status (401 vs 404/409 vs 500) to pick
 *   the right error copy, which `EventSource` hides; and
 * - `EventSource` auto-reconnects, which would re-hit an already-consumed run
 *   (the server 409s a consumed runId — a retry must always be a fresh
 *   mutation, never a reconnect).
 *
 * The reader stops at the first terminal frame and ignores anything after it.
 * There are three terminal frames — `done`, `error`, and `approval_required` —
 * and exactly one of `onDone`/`onFailure`/`onApprovalRequired` fires per call
 * (unless aborted, in which case none fires). `approval_required` ends the
 * read loop but leaves the SESSION parked server-side: the turn proposed
 * document writes, and `respondToApproval` (or a superseding message) mints a
 * fresh runId to continue. An `AbortSignal` cancels the fetch mid-stream
 * (e.g. on unmount) without surfacing a failure.
 */

export interface AgentToolStepEvent {
  tool: string;
  input?: unknown;
  ok: boolean;
  error?: string;
}

export interface AgentCitation {
  path: string;
  type: 'journal' | 'list' | 'note' | null;
  line?: number | null;
  snippet?: string | null;
}

/** Terminal `done` payload: the assistant's turn text + trace-derived citations. */
export interface AgentDonePayload {
  text: string;
  citations: AgentCitation[];
}

/** One proposed document write awaiting the user's approval. */
export interface AgentProposal {
  /** Stable proposal id (the Anthropic tool_use id). */
  id: string;
  tool: 'write_doc' | 'append_to_doc';
  path: string;
  /** The full proposed content (write_doc) or the text to append. */
  content: string;
  /** WHERE the path resolves: 'Personal' or the space name — server-resolved. */
  treeLabel: string;
  spaceId?: string | null;
}

/** Terminal `approval_required` payload: preamble text + the proposed writes. */
export interface AgentApprovalRequiredPayload {
  text: string;
  proposals: AgentProposal[];
}

/** Why a stream failed: pre-stream HTTP status, terminal `error` frame, or a drop. */
export type AgentStreamFailure =
  | { kind: 'http'; status: number }
  | { kind: 'event'; message: string; code?: string }
  | { kind: 'network' };

export interface AgentStreamHandlers {
  onToken?: (text: string) => void;
  onToolStep?: (step: AgentToolStepEvent) => void;
  onDone?: (payload: AgentDonePayload) => void;
  onFailure?: (failure: AgentStreamFailure) => void;
  /**
   * Terminal `approval_required` frame: the turn proposed document writes and
   * the session is parked awaiting the user's decision. Neither onDone nor
   * onFailure fires; `respondToApproval` mints the resume runId.
   */
  onApprovalRequired?: (payload: AgentApprovalRequiredPayload) => void;
}

export interface OpenAgentStreamOptions {
  /** Aborts the fetch/read loop; an abort never calls `onFailure`. */
  signal?: AbortSignal;
  /** Injectable for tests (jsdom has no real streaming fetch). */
  fetchImpl?: typeof fetch;
}

export interface SseFrame {
  event: string;
  data: string;
}

/**
 * Incremental SSE frame parser. Feed it decoded text chunks in arrival order;
 * it returns every complete `event:`/`data:` frame, buffering partial frames
 * across chunk boundaries.
 */
export function createSseParser(): { push: (chunk: string) => SseFrame[] } {
  let buffer = '';

  const parseBlock = (block: string): SseFrame | null => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const rawLine of block.split('\n')) {
      const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
      if (line.startsWith('event:')) {
        event = line.slice('event:'.length).replace(/^ /, '');
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
      }
    }
    if (dataLines.length === 0) return null;
    return { event, data: dataLines.join('\n') };
  };

  return {
    push(chunk: string): SseFrame[] {
      buffer += chunk;
      const frames: SseFrame[] = [];
      let separator: number;
      while ((separator = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, separator);
        buffer = buffer.slice(separator + 2);
        const frame = parseBlock(block);
        if (frame) frames.push(frame);
      }
      return frames;
    },
  };
}

/**
 * Opens the run's SSE stream and dispatches typed events to `handlers`.
 * Resolves when the stream reaches a terminal frame, fails, or is aborted.
 * Exactly one of `onDone`/`onFailure`/`onApprovalRequired` fires per call —
 * unless aborted, in which case none may fire.
 */
export async function openAgentStream(
  runId: string,
  handlers: AgentStreamHandlers,
  options: OpenAgentStreamOptions = {}
): Promise<void> {
  const { signal, fetchImpl } = options;
  const doFetch = fetchImpl ?? fetch;

  let response: Response;
  try {
    response = await doFetch(`/agent/stream/${encodeURIComponent(runId)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'text/event-stream' },
      signal,
    });
  } catch {
    if (!signal?.aborted) handlers.onFailure?.({ kind: 'network' });
    return;
  }

  if (!response.ok) {
    handlers.onFailure?.({ kind: 'http', status: response.status });
    return;
  }
  if (!response.body) {
    handlers.onFailure?.({ kind: 'network' });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();
  let terminal = false;

  const dispatch = (frame: SseFrame): void => {
    if (terminal) return;
    let data: unknown;
    try {
      data = JSON.parse(frame.data);
    } catch {
      return; // Malformed frame: skip defensively.
    }
    const payload = data as Record<string, unknown>;
    switch (frame.event) {
      case 'token':
        handlers.onToken?.(typeof payload.text === 'string' ? payload.text : '');
        break;
      case 'tool_step':
        handlers.onToolStep?.(payload as unknown as AgentToolStepEvent);
        break;
      case 'done':
        terminal = true;
        handlers.onDone?.({
          text: typeof payload.text === 'string' ? payload.text : '',
          citations: Array.isArray(payload.citations) ? (payload.citations as AgentCitation[]) : [],
        });
        break;
      case 'error':
        terminal = true;
        handlers.onFailure?.({
          kind: 'event',
          message: typeof payload.message === 'string' ? payload.message : 'Run failed',
          ...(typeof payload.code === 'string' ? { code: payload.code } : {}),
        });
        break;
      case 'approval_required':
        // Terminal, but the session stays parked: ends the read loop without
        // calling onDone/onFailure. Resumed via respondToApproval (fresh runId).
        terminal = true;
        handlers.onApprovalRequired?.({
          text: typeof payload.text === 'string' ? payload.text : '',
          proposals: Array.isArray(payload.proposals) ? (payload.proposals as AgentProposal[]) : [],
        });
        break;
      default:
        break; // Unknown event names are ignored.
    }
  };

  try {
    while (!terminal && !signal?.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
        dispatch(frame);
        if (terminal) break;
      }
    }
  } catch {
    // Read failure: an abort is silent; anything else is a dropped connection.
    if (!terminal && !signal?.aborted) handlers.onFailure?.({ kind: 'network' });
    return;
  } finally {
    try {
      void reader.cancel().catch(() => {});
    } catch {
      // reader already closed — nothing to release
    }
  }

  // Stream ended without a terminal frame (and not via abort): treat as a drop.
  if (!terminal && !signal?.aborted) handlers.onFailure?.({ kind: 'network' });
}
